import { buildURLSearchParams } from '../helpers/format';
import onPaginatedApiRequest from './ApiPaginatedRequestHandler';
import onApiRequest from './ApiRequestHandler';
import createDataProvider from './createDataProvider';
import { providerContextReducer } from './providerContextReducer';

const initialPaginatedState = {
  pageIndex: 1,
  pageSize: 50,
  totalCount: 0,
  totalPages: 0,
  totalNoFilterCount: 0,
  data: [],
  filter: null,
  loading: false,
  error: null
};

const initialState = {
  ecomCustomer: { value: null, loading: false, error: null },
  ecomVendor: { value: null, loading: false, error: null },
  ecomVendorAnalytics: { value: null, loading: false, error: null },
  ecomVendorCollections: { value: [], loading: false, error: null },
  ecomVendorCollection: { value: null, loading: false, error: null },
  ecomVendorDesigns: { value: [], loading: false, error: null },
  ecomVendorDesign: { value: null, loading: false, error: null },
  ecomVendorMedia: { value: [], loading: false, error: null },
  ecomVendors: { value: [], loading: false, error: null },
  ecomVendorResourceOptions: { value: null, loading: false, error: null },
  ecomNotifications: { value: null, loading: false, error: null },
  ecomOrder: { value: null, loading: false, error: null },
  ecomOrdersSummary: { value: null, loading: false, error: null },
  ecomStore: { value: null, loading: false, error: null },
  ecomVendorBundle: { value: null, loading: false, error: null },
  ecomVendorProducts: { value: [], loading: false, error: null },
  ecomVendorProductsExport: { value: [], loading: false, error: null },
  ecomVendorProductsVariantOptions: {
    value: null,
    loading: false,
    error: null
  },
  ecomVendorProduct: { value: null, loading: false, error: null },
  ecomVendorProductSummaryOptions: { value: null, loading: false, error: null },
  ecomVendorProductPrintDesignEdit: {
    value: null,
    loading: false,
    error: null
  },
  ecomVendorProductPrintDesignOptions: {
    value: null,
    loading: false,
    error: null
  },
  ecomVendorReport: { value: null, loading: false, error: null },
  ecomVendorSetupSummary: { value: null, loading: false, error: null },
  externalProductOptions: { value: [], loading: false, error: null },
  externalProductsImportData: { value: [], loading: false, error: null },
  paginatedEcomOrders: initialPaginatedState,
  paginatedEcomOrdersBulk: initialPaginatedState,
  paginatedEcomVendorCollections: initialPaginatedState,
  paginatedEcomVendorCustomers: initialPaginatedState,
  paginatedEcomVendorDesigns: initialPaginatedState,
  paginatedEcomVendorMedia: initialPaginatedState,
  paginatedEcomVendorStores: initialPaginatedState,
  paginatedEcomVendorBundles: initialPaginatedState,
  paginatedEcomVendorProductVariants: initialPaginatedState,
  paginatedEcomVendorProducts: initialPaginatedState,
  paginatedExternalProducts: initialPaginatedState
};

// #region Vendors
const createVendor = (dispatch) => async (
  {
    name,
    domain,
    logoImageUrl,
    supportEmail,
    sandboxEnabled,
    provinces,
    fkCurrencyType
  },
  onSuccessCallback = null,
  onErrorCallback = null
) => {
  onApiRequest(
    {
      dispatch,
      stateName: 'ecomVendors',
      url: '/api/staff/ecommerce/vendors',
      type: 'post',
      data: {
        name,
        domain,
        logoImageUrl,
        supportEmail,
        sandboxEnabled,
        provinces,
        fkCurrencyType
      }
    },
    onSuccessCallback,
    onErrorCallback
  );
};

const fetchVendorSetupSummary = (dispatch) => async (
  pkEcomVendor,
  { skipLoading } = {},
  onSuccessCallback = null,
  onErrorCallback = null
) => {
  onApiRequest(
    {
      dispatch,
      stateName: 'ecomVendorSetupSummary',
      url: `/api/ecommerce/admin/vendors/${pkEcomVendor}/setup-summary`,
      type: 'get',
      skipLoading
    },
    onSuccessCallback,
    onErrorCallback
  );
};

const fetchVendors = (dispatch) => async (
  onSuccessCallback = null,
  onErrorCallback = null
) => {
  onApiRequest(
    {
      dispatch,
      stateName: 'ecomVendors',
      url: '/api/ecommerce/vendors',
      type: 'get'
    },
    onSuccessCallback,
    onErrorCallback
  );
};

const fetchVendor = (dispatch) => async (
  pkEcomVendor,
  onSuccessCallback = null,
  onErrorCallback = null
) => {
  onApiRequest(
    {
      dispatch,
      stateName: 'ecomVendor',
      url: `/api/ecommerce/admin/vendors/${pkEcomVendor}`,
      type: 'get',
      filter: { pkEcomVendor },
      resetStateValue: null
    },
    onSuccessCallback,
    onErrorCallback
  );
};

const updateVendor = (dispatch) => async (
  pkEcomVendor,
  {
    name,
    domain,
    supportEmail,
    description,
    logoImageUrl,
    bannerImageUrl,
    primaryColor,
    primaryContrastColor,
    secondaryColor,
    secondaryAltColor,
    secondaryContrastColor,
    fkCurrencyType,
    fkRegFormSubmissionPaymentType,
    treasurerEmailDisabled,
    treasurerAlertEmail,
    ecomCustomerAddress,
    provinces,
    sandboxEnabled,
    urlHandle
  },
  onSuccessCallback = null,
  onErrorCallback = null
) => {
  onApiRequest(
    {
      dispatch,
      stateName: 'ecomVendor',
      successAction: 'success',
      url: `/api/ecommerce/admin/vendors/${pkEcomVendor}`,
      type: 'put',
      data: {
        name,
        domain,
        supportEmail,
        description,
        logoImageUrl,
        bannerImageUrl,
        primaryColor,
        primaryContrastColor,
        secondaryColor,
        secondaryAltColor,
        secondaryContrastColor,
        fkCurrencyType,
        fkRegFormSubmissionPaymentType,
        treasurerEmailDisabled,
        treasurerAlertEmail,
        ecomCustomerAddress,
        provinces,
        sandboxEnabled,
        urlHandle
      }
    },
    onSuccessCallback,
    onErrorCallback
  );
};

const updateVendorExternalConnections = (dispatch) => async (
  pkEcomVendor,
  { ecomVendorExternalConnections },
  onSuccessCallback = null,
  onErrorCallback = null
) => {
  onApiRequest(
    {
      url: `/api/ecommerce/admin/vendors/${pkEcomVendor}`,
      type: 'put',
      data: { ecomVendorExternalConnections }
    },
    onSuccessCallback,
    onErrorCallback
  );
};

const onboardVendorPaymentProvider = (dispatch) => async (
  { pkEcomVendor, accountId, updateAccount },
  onSuccessCallback = null,
  onErrorCallback = null
) => {
  onApiRequest(
    {
      url: `/api/payment/ecommerce/onboard`,
      type: 'post',
      data: {
        pkEcomVendor,
        accountId,
        updateAccount
      }
    },
    onSuccessCallback,
    onErrorCallback
  );
};

const disconnectVendorPaymentProvider = (dispatch) => async (
  { pkEcomVendor },
  onSuccessCallback = null,
  onErrorCallback = null
) => {
  onApiRequest(
    {
      url: `/api/payment/ecommerce/onboard/disconnect`,
      type: 'post',
      data: { pkEcomVendor }
    },
    onSuccessCallback,
    onErrorCallback
  );
};

const revokeEcomVendorAdmins = (dispatch) => async (
  pkEcomVendor,
  pkEcomVendorAdmins,
  onSuccessCallback = null,
  onErrorCallback = null
) => {
  onApiRequest(
    {
      dispatch,
      stateName: 'ecomVendor',
      successAction: 'success',
      skipLoading: true,
      url: `/api/ecommerce/admin/vendors/${pkEcomVendor}/admins/revoke`,
      type: 'post',
      data: pkEcomVendorAdmins
    },
    onSuccessCallback,
    onErrorCallback
  );
};

const restoreEcomVendorAdmins = (dispatch) => async (
  pkEcomVendor,
  pkEcomVendorAdmins,
  onSuccessCallback = null,
  onErrorCallback = null
) => {
  onApiRequest(
    {
      dispatch,
      stateName: 'ecomVendor',
      successAction: 'success',
      skipLoading: true,
      url: `/api/ecommerce/admin/vendors/${pkEcomVendor}/admins/restore`,
      type: 'post',
      data: pkEcomVendorAdmins
    },
    onSuccessCallback,
    onErrorCallback
  );
};

const fetchVendorResourceOptions = (dispatch) => async (
  pkEcomVendor,
  onSuccessCallback = null,
  onErrorCallback = null
) => {
  onApiRequest(
    {
      dispatch,
      stateName: 'ecomVendorResourceOptions',
      successAction: 'success',
      url: `/api/ecommerce/admin/vendors/${pkEcomVendor}/resource-options`,
      type: 'get',
      filter: { pkEcomVendor }
    },
    onSuccessCallback,
    onErrorCallback
  );
};
// #endregion

// #region Vendor Media
const fetchPaginatedVendorMedia = (dispatch) => async (
  pkEcomVendor,
  { search, sort, page, pageSize, appendResults },
  onSuccessCallback = null,
  onErrorCallback = null
) => {
  onApiRequest(
    {
      dispatch,
      stateName: 'paginatedEcomVendorMedia',
      url: `/api/ecommerce/admin/vendors/${pkEcomVendor}/media`,
      type: 'get',
      params: {
        search,
        sort,
        page,
        pageSize
      },
      successAction: appendResults ? 'paginated-append' : 'success',
      transformPayload: (data) => ({ ...data })
    },
    onSuccessCallback,
    onErrorCallback
  );
};

const fetchVendorMedia = (dispatch) => async (
  { fkEcomVendor, search, skipStateUpdate = false },
  onSuccessCallback = null,
  onErrorCallback = null
) => {
  onApiRequest(
    {
      url: '/api/ecommerce/admin/vendors/media',
      type: 'get',
      params: { fkEcomVendor, search },
      ...(skipStateUpdate
        ? {}
        : { dispatch, stateName: 'ecomVendorMedia', resetStateValue: [] })
    },
    onSuccessCallback,
    onErrorCallback
  );
};

const createVendorMedia = (dispatch) => async (
  pkEcomVendor,
  ecomVendorMedia,
  onSuccessCallback = null,
  onErrorCallback = null
) => {
  onApiRequest(
    {
      dispatch,
      stateName: 'ecomVendorMedia',
      url: `/api/ecommerce/admin/vendors/media/${pkEcomVendor}`,
      type: 'post',
      data: ecomVendorMedia
    },
    onSuccessCallback,
    onErrorCallback
  );
};

const updateVendorMedia = (dispatch) => async (
  pkEcomVendor,
  ecomVendorMedia,
  onSuccessCallback = null,
  onErrorCallback = null
) => {
  onApiRequest(
    {
      dispatch,
      stateName: 'ecomVendorMedia',
      url: `/api/ecommerce/admin/vendors/${pkEcomVendor}/media`,
      type: 'put',
      data: ecomVendorMedia,
      successAction: 'replace',
      removeFilter: (item) =>
        !ecomVendorMedia.some(
          (p) => p.pkEcomVendorMedia === item.pkEcomVendorMedia
        )
    },
    onSuccessCallback,
    onErrorCallback
  );
};

const deleteVendorMedia = (dispatch) => async (
  pkEcomVendor,
  pkEcomVendorMedia,
  onSuccessCallback = null,
  onErrorCallback = null
) => {
  const deleteVendorMediaInBulk = (page = 1) => {
    const batchSize = 100;
    const params = new URLSearchParams();
    pkEcomVendorMedia
      .slice((page - 1) * batchSize, page * batchSize)
      .forEach((pk, index) => {
        params.append(`pkEcomVendorMedia[${index}]`, pk);
      });

    onApiRequest(
      {
        dispatch,
        stateName: 'ecomVendorMedia',
        url: `/api/ecommerce/admin/vendors/${pkEcomVendor}/media?${params.toString()}`,
        type: 'delete',
        removeFilter: (item) =>
          !pkEcomVendorMedia.includes(item.pkEcomVendorMedia)
      },
      () => {
        if (page * batchSize < pkEcomVendorMedia.length) {
          deleteVendorMediaInBulk(page + 1);
        }
        else if (onSuccessCallback) {
          onSuccessCallback();
        }
      },
      onErrorCallback
    );
  };

  deleteVendorMediaInBulk();
};
// #endregion

// #region Products
const fetchPaginatedVendorProducts = (dispatch) => async (
  fkEcomVendor,
  {
    search,
    fkEcomProductStatus,
    fkEcomCategory,
    fkEcomVendorCollection,
    fkEcomExternalConnectionType,
    sort,
    page,
    pageSize,
    appendResults,
    skipLoading
  },
  onSuccessCallback = null,
  onErrorCallback = null
) => {
  onApiRequest(
    {
      dispatch,
      stateName: 'paginatedEcomVendorProducts',
      url: `/api/ecommerce/admin/vendors/products`,
      type: 'get',
      params: {
        fkEcomVendor,
        search,
        fkEcomProductStatus,
        fkEcomCategory,
        fkEcomVendorCollection,
        fkEcomExternalConnectionType,
        sort,
        page,
        pageSize,
        appendResults
      },
      successAction: appendResults ? 'paginated-append' : 'success',
      transformPayload: (data) => ({ ...data }),
      skipLoading
    },
    onSuccessCallback,
    onErrorCallback
  );
};

const fetchVendorProductOptions = (dispatch) => async (
  fkEcomVendor,
  { skipLoading },
  onSuccessCallback = null,
  onErrorCallback = null
) => {
  onApiRequest(
    {
      dispatch,
      stateName: 'ecomVendorProducts',
      url: `/api/ecommerce/admin/vendors/products/options`,
      type: 'get',
      params: { fkEcomVendor },
      successAction: 'success',
      skipLoading
    },
    onSuccessCallback,
    onErrorCallback
  );
};

const fetchVendorProductExportData = (dispatch) => async (
  {
    fkEcomVendor,
    uuids,
    urlHandles,
    search,
    fkEcomExternalConnectionType,
    fkEcomProductStatus,
    fkEcomVendorCollection,
    fkEcomVendorDesign,
    fkEcomCategory,
    skipStateUpdate = false
  },
  onSuccessCallback = null,
  onErrorCallback = null
) => {
  const fetchProductData = () =>
    new Promise((resolve, reject) => {
      onPaginatedApiRequest(
        {
          url: `/api/ecommerce/admin/vendors/products/export-data`,
          params: {
            fkEcomVendor,
            uuids,
            urlHandles,
            search,
            fkEcomExternalConnectionType,
            fkEcomProductStatus,
            fkEcomVendorCollection,
            fkEcomVendorDesign,
            fkEcomCategory,
            skipVariants: true,
            pageSize: 1000
          },
          skipSuccessStateUpdate: true,
          ...(!skipStateUpdate
            ? {
                dispatch,
                stateName: 'ecomVendorProductsExport',
                resetStateValue: []
              }
            : {})
        },
        (data) => resolve(data),
        (e) => {
          if (onErrorCallback) {
            onErrorCallback(e);
          }
          reject(e);
        }
      );
    });

  const fetchProductVariantData = () =>
    new Promise((resolve, reject) => {
      onPaginatedApiRequest(
        {
          url: `/api/ecommerce/admin/vendors/products/variants/export-data`,
          params: {
            fkEcomVendor,
            uuids,
            urlHandles,
            search,
            fkEcomExternalConnectionType,
            fkEcomProductStatus,
            fkEcomVendorCollection,
            fkEcomVendorDesign,
            fkEcomCategory,
            pageSize: 5000
          }
        },
        (variantData) => resolve(variantData),
        (e) => {
          if (onErrorCallback) {
            onErrorCallback(e);
          }
          reject(e);
        }
      );
    });

  const fetchAllData = async () => {
    const productsDataRequest = fetchProductData();
    const variantsDataRequest = fetchProductVariantData();

    const [productsData, variantsData] = await Promise.all([
      productsDataRequest,
      variantsDataRequest
    ]);

    variantsData.forEach((variant) => {
      const parentProduct = productsData.find(
        (p) =>
          p.fkEcomVendorProductVersionCurrent ===
          variant.fkEcomVendorProductVersionCurrent
      );
      if (parentProduct) {
        parentProduct.ecomVendorProductVariants.push(variant);
      }
    });

    if (!skipStateUpdate) {
      dispatch({
        type: 'success',
        stateName: 'ecomVendorProductsExport',
        payload: { value: [...productsData], loading: false }
      });
    }

    if (onSuccessCallback) {
      onSuccessCallback(productsData);
    }
  };

  fetchAllData();
};

const fetchVendorProductOptionsSummary = (dispatch) => async (
  fkEcomVendor,
  {
    search,
    fkEcomProductStatus,
    fkEcomCategory,
    fkEcomVendorCollection,
    fkEcomVendorDesign,
    fkEcomExternalConnectionType,
    uuids
  },
  onSuccessCallback = null,
  onErrorCallback = null
) => {
  const params = new URLSearchParams();
  if (search) {
    params.append(`search`, search);
    search;
  }

  uuids?.forEach((uuid, index) => {
    params.append(`uuids[${index}]`, uuid);
  });

  if (fkEcomProductStatus) {
    params.append(`fkEcomProductStatus`, fkEcomProductStatus);
  }

  if (fkEcomCategory) {
    params.append(`fkEcomCategory`, fkEcomCategory);
  }

  if (fkEcomVendorCollection) {
    params.append(`fkEcomVendorCollection`, fkEcomVendorCollection);
  }

  if (fkEcomExternalConnectionType) {
    params.append(`fkEcomExternalConnectionType`, fkEcomExternalConnectionType);
  }

  if (fkEcomVendorDesign) {
    params.append(`fkEcomVendorDesign`, fkEcomVendorDesign);
  }

  onApiRequest(
    {
      dispatch,
      stateName: 'ecomVendorProductSummaryOptions',
      url: `/api/ecommerce/admin/vendors/products/${fkEcomVendor}/variant-options?${params.toString()}`,
      type: 'get',
      resetStateValue: null,
      filter: {
        fkEcomVendor,
        search,
        fkEcomProductStatus,
        fkEcomCategory,
        fkEcomVendorCollection,
        fkEcomExternalConnectionType,
        fkEcomVendorDesign,
        uuids
      },
      successAction: 'success'
    },
    onSuccessCallback,
    onErrorCallback
  );
};

const fetchVendorProductsVariantOptions = (dispatch) => async (
  fkEcomVendor,
  {
    search,
    fkEcomProductStatus,
    fkEcomCategory,
    fkEcomVendorCollection,
    fkEcomVendorDesign,
    fkEcomExternalConnectionType,
    uuids
  },
  onSuccessCallback = null,
  onErrorCallback = null
) => {
  const params = new URLSearchParams();
  if (search) {
    params.append(`search`, search);
    search;
  }

  uuids?.forEach((uuid, index) => {
    params.append(`uuids[${index}]`, uuid);
  });

  if (fkEcomProductStatus) {
    params.append(`fkEcomProductStatus`, fkEcomProductStatus);
  }

  if (fkEcomCategory) {
    params.append(`fkEcomCategory`, fkEcomCategory);
  }

  if (fkEcomVendorCollection) {
    params.append(`fkEcomVendorCollection`, fkEcomVendorCollection);
  }

  if (fkEcomExternalConnectionType) {
    params.append(`fkEcomExternalConnectionType`, fkEcomExternalConnectionType);
  }

  if (fkEcomVendorDesign) {
    params.append(`fkEcomVendorDesign`, fkEcomVendorDesign);
  }

  onApiRequest(
    {
      dispatch,
      stateName: 'ecomVendorProductsVariantOptions',
      url: `/api/ecommerce/admin/vendors/products/${fkEcomVendor}/variant-options?${params.toString()}`,
      type: 'get',
      resetStateValue: null,
      filter: {
        fkEcomVendor,
        search,
        fkEcomProductStatus,
        fkEcomCategory,
        fkEcomVendorCollection,
        fkEcomExternalConnectionType,
        uuids
      },
      successAction: 'success'
    },
    onSuccessCallback,
    onErrorCallback
  );
};

const removeVendorProductsVariantOptions = (dispatch) => async (
  fkEcomVendor,
  { removeProductSync, filter, colorValues },
  onSuccessCallback = null,
  onErrorCallback = null
) => {
  onApiRequest(
    {
      url: `/api/ecommerce/admin/vendors/products/${fkEcomVendor}/variant-options/remove`,
      type: 'put',
      data: {
        removeProductSync,
        filter,
        colorValues
      },
      successAction: 'success'
    },
    onSuccessCallback,
    onErrorCallback
  );
};

const fetchVendorProduct = (dispatch) => async (
  productUuid,
  onSuccessCallback = null,
  onErrorCallback = null
) => {
  onApiRequest(
    {
      dispatch,
      stateName: 'ecomVendorProduct',
      url: `/api/ecommerce/admin/vendors/products/${productUuid}`,
      type: 'get',
      filter: { productUuid },
      resetStateValue: null
    },
    onSuccessCallback,
    onErrorCallback
  );
};

const createVendorProducts = (dispatch) => async (
  pkEcomVendor,
  { ecomVendorProducts, selectFirstItem },
  onSuccessCallback = null,
  onErrorCallback = null
) => {
  onApiRequest(
    {
      dispatch,
      stateName: selectFirstItem
        ? 'ecomVendorProduct'
        : 'ecomVendorProductsExport',
      url: `/api/ecommerce/admin/vendors/products/${pkEcomVendor}`,
      type: 'post',
      selectFirstItem,
      data: ecomVendorProducts,
      successAction: selectFirstItem ? 'success' : 'append'
    },
    onSuccessCallback,
    onErrorCallback
  );
};

const updateVendorProducts = (dispatch) => async (
  pkEcomVendor,
  { ecomVendorProducts, selectFirstItem, skipResponse, createProductsNotFound },
  onSuccessCallback = null,
  onErrorCallback = null
) => {
  const queryParam = skipResponse ? '?skipResponse=true' : '';
  onApiRequest(
    {
      url: `/api/ecommerce/admin/vendors/products/${pkEcomVendor}${queryParam}`,
      type: 'put',
      data: {
        ecomVendorProducts,
        createProductsNotFound
      },
      ...(!skipResponse
        ? {
            dispatch,
            stateName: selectFirstItem
              ? 'ecomVendorProduct'
              : 'ecomVendorProductsExport',
            selectFirstItem,
            successAction: selectFirstItem ? 'success' : 'replace',
            removeFilter: (item) =>
              !ecomVendorProducts.map((p) => p.uuid).includes(item.uuid)
          }
        : {})
    },
    onSuccessCallback,
    onErrorCallback
  );
};

const deleteVendorProducts = (dispatch) => async (
  pkEcomVendor,
  pkEcomVendorProducts,
  onSuccessCallback = null,
  onErrorCallback = null
) => {
  const pkEcomVendorProductsParam = pkEcomVendorProducts?.reduce(
    // eslint-disable-next-line no-return-assign
    (result, current, index) =>
      // eslint-disable-next-line no-param-reassign
      (result += `pkEcomVendorProducts[${index}]=${current}&`),
    ''
  );

  onApiRequest(
    {
      dispatch,
      stateName: 'ecomVendorProducts',
      url: `/api/ecommerce/admin/vendors/products/${pkEcomVendor}?${pkEcomVendorProductsParam}`,
      type: 'delete',
      removeFilter: (item) =>
        !pkEcomVendorProducts.includes(item.pkEcomVendorProduct)
    },
    onSuccessCallback,
    onErrorCallback
  );
};

const duplicateVendorProducts = (dispatch) => async (
  pkEcomVendor,
  { pkEcomVendorLocation, pkEcomVendorProducts, copyAll, isPrivate },
  onSuccessCallback = null,
  onErrorCallback = null
) => {
  onApiRequest(
    {
      url: `/api/ecommerce/admin/vendors/products/${pkEcomVendor}/duplicate`,
      type: 'post',
      data: {
        resourceId: pkEcomVendorLocation,
        entityIds: pkEcomVendorProducts,
        copyAll,
        isPrivate
      }
    },
    onSuccessCallback,
    onErrorCallback
  );
};

const fetchPaginatedVendorProductVariants = (dispatch) => async (
  fkEcomVendor,
  {
    trackQuantity,
    uuids,
    search,
    sku,
    barcode,
    quantity,
    sort,
    page,
    exportResponse = false,
    skipLoading = false,
    skipStateUpdate = false,
    appendResults = false
  },
  onSuccessCallback = null,
  onErrorCallback = null
) => {
  const uuidsParam = uuids?.reduce(
    // eslint-disable-next-line no-return-assign
    (result, current, index) =>
      // eslint-disable-next-line no-param-reassign
      (result += `uuids[${index}]=${current}&`),
    ''
  );

  onApiRequest(
    {
      url: `/api/ecommerce/admin/vendors/products/${fkEcomVendor}/variants${
        uuidsParam ? `?${uuidsParam}` : ''
      }`,
      type: 'get',
      successAction: appendResults ? 'paginated-append' : 'success',
      transformPayload: (data) => ({ ...data }),
      params: {
        trackQuantity,
        uuids,
        search,
        sku,
        barcode,
        quantity,
        sort,
        page,
        exportResponse
      },
      skipLoading,
      ...(skipStateUpdate
        ? {}
        : {
            dispatch,
            stateName: 'paginatedEcomVendorProductVariants',
            resetStateValue: []
          })
    },
    onSuccessCallback,
    onErrorCallback
  );
};
// #endregion

// #region Collections
const fetchPaginatedVendorCollections = (dispatch) => async (
  fkEcomVendor,
  { search, sort, page, pageSize, appendResults, skipLoading },
  onSuccessCallback = null,
  onErrorCallback = null
) => {
  onApiRequest(
    {
      dispatch,
      stateName: 'paginatedEcomVendorCollections',
      url: `/api/ecommerce/admin/vendors/${fkEcomVendor}/collections`,
      type: 'get',
      params: {
        fkEcomVendor,
        search,
        sort,
        page,
        pageSize,
        appendResults
      },
      successAction: appendResults ? 'paginated-append' : 'success',
      transformPayload: (data) => ({ ...data }),
      skipLoading
    },
    onSuccessCallback,
    onErrorCallback
  );
};

const fetchVendorCollections = (dispatch) => async (
  { fkEcomVendor },
  onSuccessCallback = null,
  onErrorCallback = null
) => {
  onApiRequest(
    {
      dispatch,
      stateName: 'ecomVendorCollections',
      url: '/api/ecommerce/admin/vendors/collections',
      type: 'get',
      params: { fkEcomVendor },
      resetStateValue: []
    },
    onSuccessCallback,
    onErrorCallback
  );
};

const fetchVendorCollection = (dispatch) => async (
  pkEcomVendorCollection,
  { skipLoading } = {},
  onSuccessCallback = null,
  onErrorCallback = null
) => {
  onApiRequest(
    {
      dispatch,
      skipLoading,
      stateName: 'ecomVendorCollection',
      url: `/api/ecommerce/admin/vendors/collections/${pkEcomVendorCollection}`,
      type: 'get',
      resetStateValue: null
    },
    onSuccessCallback,
    onErrorCallback
  );
};

const createVendorCollections = (dispatch) => async (
  pkEcomVendor,
  { ecomVendorCollections, selectFirstItem },
  onSuccessCallback = null,
  onErrorCallback = null
) => {
  onApiRequest(
    {
      dispatch,
      stateName: selectFirstItem
        ? 'ecomVendorCollection'
        : 'ecomVendorCollections',
      url: `/api/ecommerce/admin/vendors/collections/${pkEcomVendor}`,
      type: 'post',
      selectFirstItem,
      data: ecomVendorCollections,
      successAction: selectFirstItem ? 'success' : 'append'
    },
    onSuccessCallback,
    onErrorCallback
  );
};

const updateVendorCollection = (dispatch) => async (
  pkEcomVendorCollection,
  { name, description, fkEcomVendorMedia, ecomVendorCollectionProducts },
  onSuccessCallback = null,
  onErrorCallback = null
) => {
  onApiRequest(
    {
      dispatch,
      stateName: 'ecomVendorCollection',
      url: `/api/ecommerce/admin/vendors/collections/${pkEcomVendorCollection}`,
      type: 'put',
      data: {
        name,
        description,
        fkEcomVendorMedia,
        ecomVendorCollectionProducts
      },
      successAction: 'success'
    },
    onSuccessCallback,
    onErrorCallback
  );
};

const deleteVendorCollections = (dispatch) => async (
  pkEcomVendor,
  pkEcomVendorCollections,
  onSuccessCallback = null,
  onErrorCallback = null
) => {
  const pkParam = pkEcomVendorCollections?.reduce(
    // eslint-disable-next-line no-return-assign
    (result, current, index) =>
      // eslint-disable-next-line no-param-reassign
      (result += `pkEcomVendorCollections[${index}]=${current}&`),
    ''
  );

  onApiRequest(
    {
      dispatch,
      stateName: 'ecomVendorCollections',
      url: `/api/ecommerce/admin/vendors/collections/${pkEcomVendor}?${pkParam}`,
      type: 'delete',
      removeFilter: (item) =>
        !pkEcomVendorCollections.includes(item.pkEcomVendorCollection)
    },
    onSuccessCallback,
    onErrorCallback
  );
};
// #endregion

// #region Designs
const fetchPaginatedVendorDesigns = (dispatch) => async (
  fkEcomVendor,
  { search, sort, page, pageSize, appendResults, skipLoading },
  onSuccessCallback = null,
  onErrorCallback = null
) => {
  onApiRequest(
    {
      dispatch,
      stateName: 'paginatedEcomVendorDesigns',
      url: `/api/ecommerce/admin/vendors/${fkEcomVendor}/designs`,
      type: 'get',
      params: {
        fkEcomVendor,
        search,
        sort,
        page,
        pageSize,
        appendResults
      },
      successAction: appendResults ? 'paginated-append' : 'success',
      transformPayload: (data) => ({ ...data }),
      skipLoading
    },
    onSuccessCallback,
    onErrorCallback
  );
};

const fetchVendorDesigns = (dispatch) => async (
  { fkEcomVendor },
  onSuccessCallback = null,
  onErrorCallback = null
) => {
  onApiRequest(
    {
      dispatch,
      stateName: 'ecomVendorDesigns',
      url: '/api/ecommerce/admin/vendors/designs',
      type: 'get',
      params: { fkEcomVendor },
      resetStateValue: []
    },
    onSuccessCallback,
    onErrorCallback
  );
};

const fetchVendorDesign = (dispatch) => async (
  pkEcomVendorDesign,
  { skipLoading } = {},
  onSuccessCallback = null,
  onErrorCallback = null
) => {
  onApiRequest(
    {
      dispatch,
      skipLoading,
      stateName: 'ecomVendorDesign',
      url: `/api/ecommerce/admin/vendors/designs/${pkEcomVendorDesign}`,
      type: 'get',
      resetStateValue: null
    },
    onSuccessCallback,
    onErrorCallback
  );
};

const createVendorDesigns = (dispatch) => async (
  pkEcomVendor,
  { ecomVendorDesigns, selectFirstItem },
  onSuccessCallback = null,
  onErrorCallback = null
) => {
  onApiRequest(
    {
      dispatch,
      stateName: selectFirstItem ? 'ecomVendorDesign' : 'ecomVendorDesigns',
      url: `/api/ecommerce/admin/vendors/designs/${pkEcomVendor}`,
      type: 'post',
      selectFirstItem,
      data: ecomVendorDesigns,
      successAction: selectFirstItem ? 'success' : 'append'
    },
    onSuccessCallback,
    onErrorCallback
  );
};

const updateVendorDesign = (dispatch) => async (
  pkEcomVendorDesign,
  { name, description, fkEcomVendorMedia, ecomVendorDesignProducts },
  onSuccessCallback = null,
  onErrorCallback = null
) => {
  onApiRequest(
    {
      dispatch,
      stateName: 'ecomVendorDesign',
      url: `/api/ecommerce/admin/vendors/designs/${pkEcomVendorDesign}`,
      type: 'put',
      data: {
        name,
        description,
        fkEcomVendorMedia,
        ecomVendorDesignProducts
      },
      successAction: 'success'
    },
    onSuccessCallback,
    onErrorCallback
  );
};

const deleteVendorDesigns = (dispatch) => async (
  pkEcomVendor,
  pkEcomVendorDesigns,
  onSuccessCallback = null,
  onErrorCallback = null
) => {
  const pkParam = pkEcomVendorDesigns?.reduce(
    // eslint-disable-next-line no-return-assign
    (result, current, index) =>
      // eslint-disable-next-line no-param-reassign
      (result += `pkEcomVendorDesigns[${index}]=${current}&`),
    ''
  );

  onApiRequest(
    {
      dispatch,
      stateName: 'ecomVendorDesigns',
      url: `/api/ecommerce/admin/vendors/designs/${pkEcomVendor}?${pkParam}`,
      type: 'delete',
      removeFilter: (item) =>
        !pkEcomVendorDesigns.includes(item.pkEcomVendorDesign)
    },
    onSuccessCallback,
    onErrorCallback
  );
};
// #endreagion

// #region Bundles
const fetchPaginatedVendorBundles = (dispatch) => async (
  fkEcomVendor,
  {
    search,
    showStoreBundles,
    storeName,
    sort,
    page,
    pageSize,
    appendResults,
    skipLoading
  },
  onSuccessCallback = null,
  onErrorCallback = null
) => {
  onApiRequest(
    {
      dispatch,
      stateName: 'paginatedEcomVendorBundles',
      url: `/api/ecommerce/admin/vendors/${fkEcomVendor}/bundles`,
      type: 'get',
      params: {
        fkEcomVendor,
        search,
        showStoreBundles,
        storeName,
        sort,
        page,
        pageSize,
        appendResults
      },
      successAction: appendResults ? 'paginated-append' : 'success',
      transformPayload: (data) => ({ ...data }),
      skipLoading
    },
    onSuccessCallback,
    onErrorCallback
  );
};

const fetchVendorBundle = (dispatch) => async (
  fkEcomVendor,
  pkEcomVendorBundle,
  onSuccessCallback = null,
  onErrorCallback = null
) => {
  onApiRequest(
    {
      dispatch,
      stateName: 'ecomVendorBundle',
      url: `/api/ecommerce/admin/vendors/${fkEcomVendor}/bundles/${pkEcomVendorBundle}`,
      type: 'get',
      resetStateValue: null
    },
    onSuccessCallback,
    onErrorCallback
  );
};

const createVendorBundles = (dispatch) => async (
  fkEcomVendor,
  { ecomVendorBundles },
  onSuccessCallback = null,
  onErrorCallback = null
) => {
  onApiRequest(
    {
      dispatch,
      stateName: 'ecomVendorBundle',
      url: `/api/ecommerce/admin/vendors/${fkEcomVendor}/bundles`,
      type: 'post',
      selectFirstItem: true,
      data: ecomVendorBundles,
      successAction: 'success'
    },
    onSuccessCallback,
    onErrorCallback
  );
};

const updateVendorBundle = (dispatch) => async (
  pkEcomVendorBundle,
  {
    name,
    description,
    fkEcomBundleType,
    fkEcomProductStatus,
    isPrivate,
    ecomVendorBundleDiscounts,
    ecomVendorBundleProducts,
    ecomVendorProductMedia
  },
  onSuccessCallback = null,
  onErrorCallback = null
) => {
  onApiRequest(
    {
      dispatch,
      stateName: 'ecomVendorBundle',
      url: `/api/ecommerce/admin/vendors/bundles/${pkEcomVendorBundle}`,
      type: 'put',
      data: {
        name,
        description,
        fkEcomBundleType,
        fkEcomProductStatus,
        isPrivate,
        ecomVendorBundleDiscounts,
        ecomVendorBundleProducts,
        ecomVendorProductMedia
      },
      successAction: 'success'
    },
    onSuccessCallback,
    onErrorCallback
  );
};

const deleteVendorBundles = (dispatch) => async (
  pkEcomVendor,
  pkEcomVendorBundles,
  onSuccessCallback = null,
  onErrorCallback = null
) => {
  const searchParams = buildURLSearchParams({ pkEcomVendorBundles });

  onApiRequest(
    {
      type: 'delete',
      url: `/api/ecommerce/admin/vendors/${pkEcomVendor}/bundles?${searchParams.toString()}`
    },
    onSuccessCallback,
    onErrorCallback
  );
};
// #endregion

// #region Reports & Analytics
const fetchVendorAnalytics = (dispatch) => async (
  pkEcomVendor,
  { startDate, endDate, compareStartDate, compareEndDate },
  onSuccessCallback = null,
  onErrorCallback = null
) => {
  onApiRequest(
    {
      dispatch,
      stateName: 'ecomVendorAnalytics',
      url: `/api/ecommerce/admin/vendors/analytics`,
      type: 'get',
      successAction: 'success',
      params: {
        pkEcomVendor,
        startDate,
        endDate,
        compareStartDate,
        compareEndDate
      }
    },
    onSuccessCallback,
    onErrorCallback
  );
};

const generateVendorReport = (dispatch) => async (
  pkEcomStore,
  {
    uuid,
    startDate,
    endDate,
    compareStartDate,
    compareEndDate,
    periodInterval,
    groupBy,
    headers,
    options,
    filters
  },
  onSuccessCallback = null,
  onErrorCallback = null
) => {
  onApiRequest(
    {
      dispatch,
      stateName: 'ecomVendorReport',
      url: `/api/ecommerce/admin/vendors/${pkEcomStore}/reports/generate/${uuid}`,
      type: 'post',
      successAction: 'success',
      data: {
        startDate,
        endDate,
        compareStartDate,
        compareEndDate,
        periodInterval,
        groupBy,
        headers,
        options,
        filters
      }
    },
    onSuccessCallback,
    onErrorCallback
  );
};
// #endregion

// #region Customers & Orders
const fetchPaginatedVendorOrders = (dispatch) => async (
  pkEcomVendor,
  {
    search,
    store,
    status,
    sort,
    page,
    pageSize,
    isBulkShipment,
    appendResults,
    pkEcomOrders,
    skipStateUpdate
  },
  onSuccessCallback = null,
  onErrorCallback = null
) => {
  const pkEcomOrdersParam = pkEcomOrders?.reduce(
    // eslint-disable-next-line no-return-assign
    (result, current, index) =>
      // eslint-disable-next-line no-param-reassign
      (result += `pkEcomOrders[${index}]=${current}&`),
    ''
  );

  onApiRequest(
    {
      url: `/api/ecommerce/admin/vendors/${pkEcomVendor}/orders${
        pkEcomOrdersParam ? `?${pkEcomOrdersParam}` : ''
      }`,
      type: 'get',
      params: {
        search,
        store,
        status,
        sort,
        page,
        pageSize,
        isBulkShipment
      },
      transformPayload: (data) => ({ ...data }),
      ...(skipStateUpdate
        ? {}
        : {
            successAction: appendResults ? 'paginated-append' : 'success',
            dispatch,
            stateName: isBulkShipment
              ? 'paginatedEcomOrdersBulk'
              : 'paginatedEcomOrders'
          })
    },
    onSuccessCallback,
    onErrorCallback
  );
};

const fetchVendorOrdersSummary = (dispatch) => async (
  pkEcomVendor,
  onSuccessCallback = null,
  onErrorCallback = null
) => {
  onApiRequest(
    {
      dispatch,
      stateName: 'ecomOrdersSummary',
      url: `/api/ecommerce/admin/vendors/${pkEcomVendor}/orders/summary`,
      type: 'get'
    },
    onSuccessCallback,
    onErrorCallback
  );
};

const fetchVendorOrder = (dispatch) => async (
  pkEcomVendor,
  { pkEcomOrder, skipLoading },
  onSuccessCallback = null,
  onErrorCallback = null
) => {
  onApiRequest(
    {
      dispatch,
      stateName: 'ecomOrder',
      url: `/api/ecommerce/admin/vendors/${pkEcomVendor}/orders/${pkEcomOrder}`,
      type: 'get',
      filter: {
        pkEcomVendor,
        pkEcomOrder
      },
      skipLoading
    },
    onSuccessCallback,
    onErrorCallback
  );
};

const refundVendorOrder = (dispatch) => async (
  pkEcomVendor,
  { pkEcomOrder, refundAmount, skipNotification, ecomOrderReturnProducts },
  onSuccessCallback = null,
  onErrorCallback = null
) => {
  onApiRequest(
    {
      dispatch,
      stateName: 'ecomStoreCartTransactionDetails',
      url: `/api/payment/ecommerce/orders/${pkEcomOrder}/refund`,
      type: 'post',
      successAction: 'success',
      data: {
        refundAmount,
        skipNotification,
        pkEcomVendor,
        ecomOrderReturnProducts
      },
      resetStateValue: null
    },
    onSuccessCallback,
    onErrorCallback
  );
};

const cancelVendorOrder = (dispatch) => async (
  pkEcomVendor,
  {
    pkEcomOrder,
    ecomOrderReturnProducts,
    cancelOrder,
    refundPayment,
    refundAmount,
    reason
  },
  onSuccessCallback = null,
  onErrorCallback = null
) => {
  onApiRequest(
    {
      dispatch,
      stateName: 'ecomStoreCartTransactionDetails',
      url: `/api/payment/ecommerce/orders/${pkEcomOrder}/cancel`,
      type: 'post',
      successAction: 'success',
      data: {
        pkEcomVendor,
        ecomOrderReturnProducts,
        cancelOrder,
        refundPayment,
        refundAmount,
        reason
      },
      resetStateValue: null
    },
    onSuccessCallback,
    onErrorCallback
  );
};

const fulfillEcomVendorOrderProducts = (dispatch) => async (
  pkEcomVendor,
  ecomOrderShippings,
  onSuccessCallback = null,
  onErrorCallback = null
) => {
  onApiRequest(
    {
      url: `/api/ecommerce/admin/vendors/${pkEcomVendor}/orders/fulfill`,
      type: 'post',
      data: ecomOrderShippings
    },
    onSuccessCallback,
    onErrorCallback
  );
};

const updateEcomOrderShippings = (dispatch) => async (
  pkEcomVendor,
  ecomOrderShippings,
  onSuccessCallback = null,
  onErrorCallback = null
) => {
  onApiRequest(
    {
      url: `/api/ecommerce/admin/vendors/${pkEcomVendor}/orders/shipping`,
      type: 'put',
      data: ecomOrderShippings
    },
    onSuccessCallback,
    onErrorCallback
  );
};

const notifyEcomOrderShipping = (dispatch) => async (
  pkEcomVendor,
  { pkEcomOrderShippings },
  onSuccessCallback = null,
  onErrorCallback = null
) => {
  onApiRequest(
    {
      url: `/api/ecommerce/admin/vendors/${pkEcomVendor}/orders/shipping/notify`,
      type: 'post',
      data: pkEcomOrderShippings
    },
    onSuccessCallback,
    onErrorCallback
  );
};

const acceptEcomOrder = (dispatch) => async (
  pkEcomVendor,
  { pkEcomOrders },
  onSuccessCallback = null,
  onErrorCallback = null
) => {
  onApiRequest(
    {
      url: `/api/ecommerce/admin/vendors/${pkEcomVendor}/orders/accept`,
      type: 'post',
      data: pkEcomOrders
    },
    onSuccessCallback,
    onErrorCallback
  );
};
// #endregion

// region Customers
const fetchPaginatedVendorCustomers = (dispatch) => async (
  pkEcomVendor,
  { search, sort, page, pageSize, appendResults, skipLoading },
  onSuccessCallback = null,
  onErrorCallback = null
) => {
  onApiRequest(
    {
      dispatch,
      stateName: 'paginatedEcomVendorCustomers',
      url: `/api/ecommerce/admin/vendors/${pkEcomVendor}/customers`,
      type: 'get',
      params: {
        search,
        sort,
        page,
        pageSize,
        appendResults
      },
      successAction: appendResults ? 'paginated-append' : 'success',
      transformPayload: (data) => ({ ...data }),
      skipLoading
    },
    onSuccessCallback,
    onErrorCallback
  );
};

const fetchVendorCustomer = (dispatch) => async (
  pkEcomVendor,
  { pkEcomCustomer },
  onSuccessCallback = null,
  onErrorCallback = null
) => {
  onApiRequest(
    {
      dispatch,
      stateName: 'ecomCustomer',
      url: `/api/ecommerce/admin/vendors/${pkEcomVendor}/customers/${pkEcomCustomer}`,
      type: 'get',
      params: {},
      successAction: 'success'
    },
    onSuccessCallback,
    onErrorCallback
  );
};
// #endregion

// region Merchants
const fetchPaginatedVendorStores = (dispatch) => async (
  pkEcomVendor,
  { search, sort, page, pageSize, appendResults, skipLoading },
  onSuccessCallback = null,
  onErrorCallback = null
) => {
  onApiRequest(
    {
      dispatch,
      stateName: 'paginatedEcomVendorStores',
      url: `/api/ecommerce/admin/vendors/${pkEcomVendor}/stores`,
      type: 'get',
      params: {
        search,
        sort,
        page,
        pageSize,
        appendResults
      },
      successAction: appendResults ? 'paginated-append' : 'success',
      transformPayload: (data) => ({ ...data }),
      skipLoading
    },
    onSuccessCallback,
    onErrorCallback
  );
};

const fetchVendorStore = (dispatch) => async (
  pkEcomVendor,
  { pkEcomStore },
  onSuccessCallback = null,
  onErrorCallback = null
) => {
  onApiRequest(
    {
      dispatch,
      stateName: 'ecomStore',
      url: `/api/ecommerce/admin/vendors/${pkEcomVendor}/stores/${pkEcomStore}`,
      type: 'get',
      params: {},
      successAction: 'success'
    },
    onSuccessCallback,
    onErrorCallback
  );
};
// #endregion

// #region Notifications
const fetchVendorNotifications = (dispatch) => async (
  pkEcomVendor,
  onSuccessCallback = null,
  onErrorCallback = null
) => {
  onApiRequest(
    {
      dispatch,
      stateName: 'ecomNotifications',
      url: `/api/ecommerce/admin/vendors/${pkEcomVendor}/notifications`,
      type: 'get',
      successAction: 'success',
      filter: { pkEcomVendor }
    },
    onSuccessCallback,
    onErrorCallback
  );
};
// #endregion

// #region External connections
const fetchExternalProducts = (dispatch) => async (
  pkEcomVendor,
  { fkEcomExternalConnectionType, externalId, includeOptions, page, pageSize },
  onSuccessCallback = null,
  onErrorCallback = null
) => {
  onApiRequest(
    {
      url: `/api/ecommerce/admin/vendor/products/${pkEcomVendor}/external`,
      type: 'get',
      params: {
        fkEcomExternalConnectionType,
        externalId,
        includeOptions,
        page,
        pageSize
      }
    },
    (d) => {
      dispatch({
        type: page && page > 1 ? 'paginated-append' : 'success',
        stateName: 'paginatedExternalProducts',
        payload: { ...d.paginatedExternalProducts.data }
      });

      if (d.paginatedExternalProducts.options) {
        dispatch({
          type: 'success',
          stateName: 'externalProductOptions',
          payload: { value: d.paginatedExternalProducts.options }
        });
      }

      if (onSuccessCallback) {
        onSuccessCallback(d);
      }
    },
    onErrorCallback
  );
};

const importExternalProducts = (dispatch) => async (
  pkEcomVendor,
  {
    ecomExternalConnectionExternalId,
    fkEcomExternalConnectionType,
    products,
    syncOptions
  },
  onSuccessCallback = null,
  onErrorCallback = null
) => {
  onApiRequest(
    {
      url: `/api/ecommerce/admin/vendor/products/${pkEcomVendor}/external/import`,
      type: 'post',
      data: {
        ecomExternalConnectionExternalId,
        fkEcomExternalConnectionType,
        products,
        syncOptions
      }
    },
    onSuccessCallback,
    onErrorCallback
  );
};

const fetchExternalProductsImportData = (dispatch) => async (
  pkEcomVendor,
  { fkEcomExternalConnectionType, externalProductIds },
  onSuccessCallback = null,
  onErrorCallback = null
) => {
  const externalProductIdsParam = externalProductIds?.reduce(
    // eslint-disable-next-line no-return-assign
    (result, current, index) =>
      // eslint-disable-next-line no-param-reassign
      (result += `externalProductIds[${index}]=${current}&`),
    ''
  );

  onApiRequest(
    {
      dispatch,
      stateName: 'externalProductsImportData',
      successAction: 'success',
      url: `/api/ecommerce/admin/vendor/products/${pkEcomVendor}/external/details?fkEcomExternalConnectionType=${fkEcomExternalConnectionType}&${externalProductIdsParam}`,
      type: 'get'
    },
    onSuccessCallback,
    onErrorCallback
  );
};

const fetchVendorProductPrintDesigns = (dispatch) => async (
  pkEcomVendor,
  { uuids, includeAllDesigns },
  onSuccessCallback = null,
  onErrorCallback = null
) => {
  const uuidsParam = uuids?.reduce(
    // eslint-disable-next-line no-return-assign
    (result, current, index) =>
      // eslint-disable-next-line no-param-reassign
      (result += `uuids[${index}]=${current}&`),
    ''
  );

  onApiRequest(
    {
      dispatch,
      stateName: 'ecomVendorProductPrintDesignEdit',
      successAction: 'success',
      url: `/api/ecommerce/admin/vendor/products/${pkEcomVendor}/designs?includeAllDesigns=${includeAllDesigns}${
        uuidsParam ? `&${uuidsParam}` : ''
      }`,
      type: 'get'
    },
    onSuccessCallback,
    onErrorCallback
  );
};

const fetchVendorProductPrintDesignOptions = (dispatch) => async (
  pkEcomVendor,
  onSuccessCallback = null,
  onErrorCallback = null
) => {
  onApiRequest(
    {
      dispatch,
      stateName: 'ecomVendorProductPrintDesignOptions',
      successAction: 'success',
      url: `/api/ecommerce/admin/vendor/products/${pkEcomVendor}/designs/options`,
      type: 'get'
    },
    onSuccessCallback,
    onErrorCallback
  );
};

const regenerateVendorProductDesignImages = (dispatch) => async (
  pkEcomVendor,
  regenerateRequests,
  onSuccessCallback = null,
  onErrorCallback = null
) => {
  onApiRequest(
    {
      url: `/api/ecommerce/admin/vendor/products/${pkEcomVendor}/designs/regenerate`,
      type: 'post',
      data: regenerateRequests
    },
    onSuccessCallback,
    onErrorCallback
  );
};

const createVendorProductsFromPrintTemplates = (dispatch) => async (
  pkEcomVendor,
  { productDesigns },
  onSuccessCallback = null,
  onErrorCallback = null
) => {
  onApiRequest(
    {
      url: `/api/ecommerce/admin/vendor/products/${pkEcomVendor}/designs`,
      type: 'post',
      data: productDesigns.map((d) => ({ ...d, includePrintImages: true }))
    },
    (data) => {
      onApiRequest(
        {
          url: `/api/ecommerce/admin/vendor/products/${pkEcomVendor}/designs/regenerate`,
          type: 'post',
          data: data.map((d) => ({
            pkEcomVendorProduct: d.pkEcomVendorProduct,
            includeProductImages: true
          }))
        },
        onSuccessCallback,
        onErrorCallback
      );
    },
    onErrorCallback
  );
};

const getVendorProductPrintTemplatePreviews = (dispatch) => async (
  pkEcomVendor,
  { renderRequests },
  onSuccessCallback = null,
  onErrorCallback = null
) => {
  onApiRequest(
    {
      url: `/api/ecommerce/admin/vendor/products/${pkEcomVendor}/designs/preview`,
      type: 'post',
      data: renderRequests
    },
    onSuccessCallback,
    onErrorCallback
  );
};
// #endregion

export const { Provider, Context } = createDataProvider(
  providerContextReducer,
  {
    acceptEcomOrder,
    cancelVendorOrder,
    createVendor,
    createVendorBundles,
    createVendorCollections,
    createVendorDesigns,
    createVendorMedia,
    createVendorProducts,
    createVendorProductsFromPrintTemplates,
    deleteVendorBundles,
    deleteVendorCollections,
    deleteVendorDesigns,
    deleteVendorMedia,
    deleteVendorProducts,
    disconnectVendorPaymentProvider,
    duplicateVendorProducts,
    fetchPaginatedVendorMedia,
    fetchPaginatedVendorProducts,
    fetchVendor,
    fetchVendorAnalytics,
    fetchVendorBundle,
    fetchVendorCollection,
    fetchVendorCollections,
    fetchVendorDesign,
    fetchVendorDesigns,
    fetchVendorCustomer,
    fetchVendorMedia,
    fetchVendorNotifications,
    fetchVendorResourceOptions,
    fetchVendors,
    fetchVendorOrder,
    fetchVendorOrdersSummary,
    fetchVendorProduct,
    fetchVendorProductOptions,
    fetchVendorProductExportData,
    fetchVendorProductPrintDesigns,
    fetchVendorProductPrintDesignOptions,
    fetchVendorProductOptionsSummary,
    fetchVendorProductsVariantOptions,
    fetchVendorSetupSummary,
    fetchVendorStore,
    fetchPaginatedVendorCustomers,
    fetchPaginatedVendorOrders,
    fetchPaginatedVendorBundles,
    fetchPaginatedVendorCollections,
    fetchPaginatedVendorDesigns,
    fetchPaginatedVendorProductVariants,
    fetchPaginatedVendorStores,
    fetchExternalProducts,
    fetchExternalProductsImportData,
    fulfillEcomVendorOrderProducts,
    getVendorProductPrintTemplatePreviews,
    generateVendorReport,
    importExternalProducts,
    notifyEcomOrderShipping,
    onboardVendorPaymentProvider,
    updateEcomOrderShippings,
    updateVendor,
    updateVendorBundle,
    updateVendorCollection,
    updateVendorDesign,
    updateVendorMedia,
    updateVendorExternalConnections,
    updateVendorProducts,
    refundVendorOrder,
    regenerateVendorProductDesignImages,
    removeVendorProductsVariantOptions,
    restoreEcomVendorAdmins,
    revokeEcomVendorAdmins
  },
  initialState
);
